import {
  LEGACY_RISK_MATRIX,
  RA_MASTER_KINDS,
  RA_MASTER_STATUSES,
  RA_VESSEL_GROUPS,
  type RaMasterKind,
  raLinkRatings,
} from '@felix/domain';
import { Button, Select, Tabs, TextInput } from '@mantine/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconFileImport,
  IconLibrary,
  IconListCheck,
  IconPlus,
  IconSearch,
  IconShieldCheck,
  IconSitemap,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../../EmptyState.js';
import { useFitColumns } from '../../../fit-columns.js';
import { PageHeader } from '../../../PageHeader.js';
import { Panel } from '../../../Panel.js';
import { StatTile } from '../../../StatTile.js';
import table from '../../materials/Materials.module.css';
import { errorCode } from '../../personnel/source.js';
import purchasing from '../../purchasing/Purchasing.module.css';
import { sortItems } from '../../purchasing/requisitions.js';
import {
  LoadError,
  NoMatch,
  SortableTh,
  type SortState,
  TablePager,
  TableSkeleton,
} from '../../purchasing/table.js';
import { DefaultLibraryDialog } from './DefaultLibraryDialog.js';
import { type ActivityReviewInput, ReviewQueuePanel } from './FinalReview.js';
import { ImportDialog } from './ImportDialog.js';
import { MasterDrawer } from './MasterDrawer.js';
import { columnsFor } from './MasterTables.js';
import classes from './RaMaster.module.css';
import type { RaMasterItem, RaMasterSource, RaMasterView } from './types.js';
import { draftsOf, EMPTY_MASTER_FILTER, filterMaster, type MasterFilter } from './view.js';

const PAGE_SIZE = 15;

type Notice = { tone: 'success' | 'danger'; text: string };
type Drawer = { kind: RaMasterKind; item: RaMasterItem<RaMasterKind> | null } | null;

export interface RaMasterDataScreenProps {
  source: RaMasterSource;
}

/**
 * Compliance ▸ Risk Assessment ▸ Master Data (K2; ADR 0086 §7). Şirketin risk ana verisi: altı
 * sekme (operasyon alanı, faaliyet, tehlike, sonuç, kontrol, risk satırı), arama ve süzgeç,
 * sıralama ve sayfalama. Ofiste yeni kayıt, düzenleme, etkinleştirme, pasifleme ve Excel içe
 * aktarma; gemide salt okunur. Tasarım yok; tablo dili Port Distance Library.
 */
export function RaMasterDataScreen({ source }: RaMasterDataScreenProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<
    { kind: 'loading' } | { kind: 'error'; code: string } | { kind: 'ready'; view: RaMasterView }
  >({ kind: 'loading' });
  const load = useCallback(async () => {
    try {
      setState({ kind: 'ready', view: await source.load() });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source]);
  useEffect(() => {
    void load();
  }, [load]);
  return (
    <div className={purchasing.screen}>
      <PageHeader title={t('riskMaster.title')} description={t('riskMaster.description')} />
      {state.kind === 'error' ? (
        <Panel>
          <LoadError
            title={t('riskMaster.empty.error')}
            code={state.code}
            onRetry={() => void load()}
          />
        </Panel>
      ) : state.kind === 'loading' ? (
        <Panel flush aria-label={t('riskMaster.title')}>
          <TableSkeleton columns={6} label={t('riskMaster.title')} />
        </Panel>
      ) : (
        <RaMasterDataView view={state.view} source={source} onReload={load} />
      )}
    </div>
  );
}

/** Yüklenmiş görünüm (ekran testi doğrudan çizer). */
export function RaMasterDataView({
  view,
  source,
  onReload,
}: {
  view: RaMasterView;
  source: RaMasterSource;
  onReload: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) => t(`riskMaster.${key}`, options);
  const editor = view.can.edit ? source.editor : undefined;
  const matrix = view.matrix?.definition ?? LEGACY_RISK_MATRIX;
  // `queue`: FINAL karar kuyruğu sekmesi (ADR 0098 §6), yalnız ofiste ve kayıt varken
  const [tab, setTab] = useState<RaMasterKind | 'queue'>('activities');
  const masterTab: RaMasterKind = tab === 'queue' ? 'activities' : tab;
  const tasks = view.reviewTasks ?? [];
  const [filter, setFilter] = useState<MasterFilter>(EMPTY_MASTER_FILTER);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState<string>>({ key: 'code', dir: 'asc' });
  const [drawer, setDrawer] = useState<Drawer>(null);
  const [importing, setImporting] = useState(false);
  // Ürünün varsayılan RA kütüphanesinden çekme (ADR 0106)
  const [browsing, setBrowsing] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [busy, setBusy] = useState(false);
  const [drawerError, setDrawerError] = useState<string | null>(null);
  const tableBox = useFitColumns<HTMLElement>();

  const errorText = (error: unknown) => {
    const code = errorCode(error);
    const detail = (error as { detail?: unknown } | null)?.detail;
    return t(`riskMaster.errors.${code}`, {
      detail: typeof detail === 'string' ? detail : '',
      defaultValue: t('riskMaster.errors.GENERIC'),
    });
  };

  const columns = useMemo(
    () =>
      columnsFor(masterTab, {
        view,
        t,
        matrix,
        open: (item) => {
          setDrawerError(null);
          setDrawer({ kind: masterTab, item });
        },
      }),
    [masterTab, view, t, matrix],
  );
  const items = useMemo(() => {
    const found = filterMaster(view, masterTab, filter);
    const column = columns.find((c) => c.key === sort.key && c.sort) ?? columns[0];
    return column?.sort ? sortItems(found, sort.dir, column.sort) : found;
  }, [view, masterTab, filter, sort, columns]);
  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = items.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const drafts = draftsOf(items);
  const all = view[masterTab] as unknown as RaMasterItem<RaMasterKind>[];

  const stats = useMemo(() => {
    const high = view.links.filter((l) => {
      const level = raLinkRatings(l, matrix).initial?.level;
      return level === 'high' || level === 'very_high' || level === 'critical';
    }).length;
    return {
      activeActivities: view.activities.filter((a) => a.status === 'active').length,
      draftActivities: view.activities.filter((a) => a.status === 'draft').length,
      families: new Set(view.hazards.map((h) => h.family ?? h.code)).size,
      existing: view.controls.filter((c) => c.stage === 'existing').length,
      additional: view.controls.filter((c) => c.stage === 'additional').length,
      high,
    };
  }, [view, matrix]);

  const changeFilter = (patch: Partial<MasterFilter>) => {
    setFilter((f) => ({ ...f, ...patch }));
    setPage(1);
  };

  const run = async (action: () => Promise<string>, inDrawer: boolean) => {
    setBusy(true);
    setDrawerError(null);
    try {
      const text = await action();
      setNotice({ tone: 'success', text });
      setDrawer(null);
      await onReload();
    } catch (error) {
      if (inDrawer) setDrawerError(errorText(error));
      else setNotice({ tone: 'danger', text: errorText(error) });
    } finally {
      setBusy(false);
    }
  };

  const groupFilter = masterTab === 'categories' || masterTab === 'activities';
  const categoryFilter = masterTab === 'activities' || masterTab === 'links';

  return (
    <>
      {source.scope === 'ship' ? <p className={classes.note}>{k('shipNote')}</p> : null}
      {notice ? (
        <p
          className={purchasing.notice}
          data-tone={notice.tone}
          role={notice.tone === 'danger' ? 'alert' : 'status'}
        >
          {notice.text}
        </p>
      ) : null}
      <div className={classes.stats}>
        <StatTile
          icon={<IconSitemap size={22} />}
          label={k('stats.activities')}
          value={view.activities.length}
          variant="plain"
        />
        <StatTile
          icon={<IconAlertTriangle size={22} />}
          label={k('stats.hazards')}
          value={view.hazards.length}
          variant="plain"
        />
        <StatTile
          icon={<IconShieldCheck size={22} />}
          label={k('stats.controls')}
          value={view.controls.length}
          variant="plain"
        />
        <StatTile
          icon={<IconListCheck size={22} />}
          label={k('stats.links')}
          value={view.links.length}
          tone={stats.high > 0 ? 'warning' : 'default'}
          variant="plain"
        />
      </div>
      <p className={classes.hint}>
        {k('stats.activitiesNote', {
          active: stats.activeActivities,
          draft: stats.draftActivities,
        })}{' '}
        · {k('stats.hazardsNote', { count: stats.families })} ·{' '}
        {k('stats.controlsNote', { existing: stats.existing, additional: stats.additional })} ·{' '}
        {k('stats.linksNote', { high: stats.high })}
      </p>
      <Panel flush aria-label={k('title')}>
        <Tabs
          className={classes.tabs}
          value={tab}
          onChange={(value) => {
            if (!value) return;
            setTab(value as RaMasterKind | 'queue');
            setFilter(EMPTY_MASTER_FILTER);
            setSort({ key: 'code', dir: 'asc' });
            setPage(1);
          }}
        >
          <Tabs.List>
            {RA_MASTER_KINDS.map((kind) => (
              <Tabs.Tab key={kind} value={kind}>
                {k(`tabs.${kind}`)}
                <span className={classes.count}>{view[kind].length}</span>
              </Tabs.Tab>
            ))}
            {tasks.length > 0 ? (
              <Tabs.Tab value="queue">
                {k('tabs.queue')}
                <span className={classes.count}>
                  {tasks.filter((task) => task.status === 'open').length}
                </span>
              </Tabs.Tab>
            ) : null}
          </Tabs.List>
        </Tabs>
        {tab === 'queue' ? (
          <ReviewQueuePanel
            tasks={tasks}
            editable={editor?.decideTask !== undefined}
            {...(editor?.decideTask
              ? {
                  onDecide: async (task, input) => {
                    await editor.decideTask?.(task.id, task.rowVersion, input);
                    setNotice({ tone: 'success', text: k('queue.saved', { code: task.code }) });
                    await onReload();
                  },
                }
              : {})}
          />
        ) : (
          <>
            <div className={classes.toolbar}>
              <TextInput
                className={classes.search}
                leftSection={<IconSearch size={16} aria-hidden />}
                placeholder={k('search')}
                aria-label={k('search')}
                value={filter.query}
                onChange={(event) => changeFilter({ query: event.currentTarget.value })}
              />
              <Select
                className={classes.filter}
                aria-label={k('filter.status')}
                allowDeselect={false}
                value={filter.status}
                data={[
                  { value: 'all', label: k('filter.allStatuses') },
                  ...RA_MASTER_STATUSES.map((s) => ({ value: s, label: k(`statuses.${s}`) })),
                ]}
                onChange={(value) =>
                  changeFilter({ status: (value ?? 'all') as MasterFilter['status'] })
                }
              />
              {groupFilter ? (
                <Select
                  className={classes.filter}
                  aria-label={k('filter.group')}
                  allowDeselect={false}
                  value={filter.group}
                  data={[
                    { value: 'all', label: k('filter.allGroups') },
                    ...RA_VESSEL_GROUPS.map((g) => ({ value: g, label: k(`groups.${g}`) })),
                  ]}
                  onChange={(value) => changeFilter({ group: value ?? 'all' })}
                />
              ) : null}
              {categoryFilter ? (
                <Select
                  className={classes.filter}
                  aria-label={k('filter.category')}
                  allowDeselect={false}
                  searchable
                  value={filter.category}
                  data={[
                    { value: 'all', label: k('filter.allCategories') },
                    ...view.categories.map((c) => ({ value: c.id, label: c.name })),
                  ]}
                  onChange={(value) => changeFilter({ category: value ?? 'all' })}
                />
              ) : null}
              {editor ? (
                <div className={classes.actions}>
                  {view.can.import && editor.defaultLibrary ? (
                    <Button
                      variant="default"
                      leftSection={<IconLibrary size={16} />}
                      onClick={() => setBrowsing(true)}
                    >
                      {k('actions.defaultLibrary')}
                    </Button>
                  ) : null}
                  {view.can.import ? (
                    <Button
                      variant="default"
                      leftSection={<IconFileImport size={16} />}
                      onClick={() => setImporting(true)}
                    >
                      {k('actions.import')}
                    </Button>
                  ) : null}
                  {drafts.length > 0 ? (
                    <Button
                      variant="light"
                      leftSection={<IconCheck size={16} />}
                      loading={busy}
                      onClick={() =>
                        void run(async () => {
                          const count = await editor.setStatus(
                            masterTab,
                            drafts.map((d) => ({ id: d.id, rowVersion: d.rowVersion })),
                            'active',
                          );
                          return k('actions.activated', { count });
                        }, false)
                      }
                    >
                      {k('actions.activateDrafts', { count: drafts.length })}
                    </Button>
                  ) : null}
                  <Button
                    leftSection={<IconPlus size={16} />}
                    onClick={() => {
                      setDrawerError(null);
                      setDrawer({ kind: masterTab, item: null });
                    }}
                  >
                    {k('actions.new')}
                  </Button>
                </div>
              ) : null}
            </div>
            {all.length === 0 ? (
              <EmptyState
                title={k('empty.title')}
                description={editor ? k('empty.office') : k('empty.ship')}
              />
            ) : items.length === 0 ? (
              <NoMatch
                title={k('empty.noMatch')}
                onClear={() => {
                  setFilter(EMPTY_MASTER_FILTER);
                  setPage(1);
                }}
              />
            ) : (
              <>
                <section
                  ref={tableBox}
                  className={table.tableWrap}
                  aria-label={k('tableLabel', { tab: k(`tabs.${masterTab}`) })}
                  // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
                  tabIndex={0}
                >
                  <table className={`${table.table} ${purchasing.table}`}>
                    <thead>
                      <tr>
                        {columns.map((column) =>
                          column.sort ? (
                            <SortableTh
                              key={column.key}
                              column={column.key}
                              label={column.label}
                              sort={sort}
                              onSort={(next) => {
                                setSort(next);
                                setPage(1);
                              }}
                              numeric={column.numeric === true}
                              drop={column.drop}
                            />
                          ) : (
                            <th
                              key={column.key}
                              scope="col"
                              data-numeric={column.numeric || undefined}
                              data-drop={column.drop}
                            >
                              {column.label}
                            </th>
                          ),
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map((item) => (
                        <tr key={item.id}>
                          {columns.map((column) => (
                            <td
                              key={column.key}
                              data-numeric={column.numeric || undefined}
                              data-drop={column.drop}
                              className={column.wrap ? classes.wrapCell : undefined}
                            >
                              {column.render(item)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
                <TablePager
                  page={current}
                  pages={pages}
                  summary={k('showing', {
                    from: (current - 1) * PAGE_SIZE + 1,
                    to: Math.min(current * PAGE_SIZE, items.length),
                    total: items.length,
                  })}
                  label={k('pagination')}
                  onPage={setPage}
                />
              </>
            )}
          </>
        )}
      </Panel>
      {drawer ? (
        <MasterDrawer
          key={`${drawer.kind}-${drawer.item?.id ?? 'new'}`}
          kind={drawer.kind}
          item={drawer.item}
          view={view}
          matrix={matrix}
          editable={editor !== undefined}
          busy={busy}
          error={drawerError}
          onClose={() => setDrawer(null)}
          {...(editor
            ? {
                onSave: (content: Record<string, unknown>) =>
                  void run(async () => {
                    const saved = await editor.save(
                      drawer.kind,
                      drawer.item?.id ?? null,
                      drawer.item?.rowVersion ?? null,
                      content as never,
                    );
                    return t('riskMaster.form.saved', {
                      code: (saved as unknown as { code: string }).code,
                    });
                  }, true),
                onStatus: (status: 'active' | 'retired') =>
                  void run(async () => {
                    const item = drawer.item;
                    if (!item) return '';
                    await editor.setStatus(
                      drawer.kind,
                      [{ id: item.id, rowVersion: item.rowVersion }],
                      status,
                    );
                    const code = (item as unknown as { code: string }).code;
                    return status === 'retired'
                      ? k('actions.retired', { code })
                      : k('actions.reactivated', { code });
                  }, true),
                ...(editor.reviewActivity && view.can.approve !== false
                  ? {
                      onReview: (input: ActivityReviewInput) =>
                        void run(async () => {
                          const item = drawer.item;
                          if (!item) return '';
                          await editor.reviewActivity?.(item.id, item.rowVersion, input);
                          return k('review.saved', {
                            code: (item as unknown as { code: string }).code,
                            status: k(`review.statuses.${input.status}`),
                          });
                        }, true),
                    }
                  : {}),
              }
            : {})}
        />
      ) : null}
      {browsing && editor?.defaultLibrary ? (
        <DefaultLibraryDialog
          library={editor.defaultLibrary}
          onClose={() => setBrowsing(false)}
          onDone={(result) => {
            setNotice({
              tone: 'success',
              text: result.applied
                ? k('defaultLibrary.added', { count: result.added.length })
                : k('defaultLibrary.nothingAdded'),
            });
            setBrowsing(false);
            void onReload();
          }}
        />
      ) : null}
      {importing && editor ? (
        <ImportDialog
          editor={editor}
          onClose={() => setImporting(false)}
          onDone={(result) => {
            const add = Object.values(result.plan.counts).reduce((n, c) => n + c.add, 0);
            const update = Object.values(result.plan.counts).reduce((n, c) => n + c.update, 0);
            setNotice({
              tone: 'success',
              text: result.applied
                ? t('riskMaster.import.applied', { add, update })
                : t('riskMaster.import.nothingToDo'),
            });
            setImporting(false);
            void onReload();
          }}
        />
      ) : null}
    </>
  );
}
